import { forwardRef, useEffect, useImperativeHandle, useReducer } from 'react';

import { faCloudBolt } from '@fortawesome/pro-duotone-svg-icons';
import {
    faCalendarXmark,
    faCar,
    faCity,
    faClock,
    faHandshake,
    faMapLocationDot,
    faPencil,
    faPlateUtensils,
} from '@fortawesome/pro-solid-svg-icons';
import {
    IonGrid,
    IonInput,
    IonItem,
    IonLabel,
    IonList,
    IonListHeader,
    IonRow,
    IonTextarea,
    IonToggle,
    useIonToast,
} from '@ionic/react';
import { __, filter, find, includes, map, some, toInteger } from 'lodash/fp';
import { DateTime } from 'luxon';

import { getImplicitPlatform } from '../../App';
import {
    adjustMadsPropertiesByTime,
    useAppConfig,
    useCompanies,
    useEmployees,
    useMads,
    useWorkPlaces,
} from '../../hooks/useSwr';
import { Pointing } from '../../types/models';
import { defaultToastOptions } from '../../utils/Global';
import { hoursToDuration, stringTimeToFloat } from '../../utils/date';
import {
    LunchConstraints,
    PointingValidator,
    ViewModeConstraints,
    WorkplaceConstraints,
} from '../../utils/pointingConstraints';
import { RoundedSquareIcon, TriangleIcon } from '../Icon';
import { ItemWithHoursPicker, ItemWithNumberPicker } from '../ItemWithPicker';
import { Many2OneSelector } from '../Many2OneSelector';
import TimeRangePicker from '../TimeRangePicker';
import { PointingDetailHandle, PointingDetailProps, ViewMode, VisibilityField } from '../common/PointingDetail';

export const PointingDetailMobile: React.ForwardRefRenderFunction<PointingDetailHandle, PointingDetailProps> = (
    { initialPointing, isDisabled = false, viewMode, fieldsToHighlight, highlightColor = 'rgb(254 226 226)', onChange },
    ref,
) => {
    const { dataWithPlacehoder: madsPlaceholder, data: mads } = useMads();
    const { data: appConfig } = useAppConfig();
    const { dataWithPlacehoder: workPlaces } = useWorkPlaces();
    const { dataWithDisplayName: employees } = useEmployees();
    const { data: companies } = useCompanies();
    const [presentToast] = useIonToast();

    // Create validator instance with current context
    const validator = new PointingValidator({
        appConfig,
        mads,
        workPlaces,
        employees,
        currentDate: initialPointing.date,
    });

    const [pointing, editPointing] = useReducer((prev: Pointing, next: Partial<Pointing>) => {
        const validationResult = validator.validatePointingUpdate(prev, next, viewMode);

        if (!validationResult.isValid && validationResult.errorMessage) {
            presentToast({
                ...defaultToastOptions,
                message: validationResult.errorMessage,
                duration: 4000,
                color: 'danger',
            });
            // Return previous state without changes if validation fails
            return prev;
        }

        let newPointing = { ...prev, ...next };

        // Apply auto-assignments from the validation result
        if (validationResult.autoAssignments) {
            const autoAssignments = validationResult.autoAssignments;

            if (autoAssignments.employee_id !== undefined) {
                newPointing.employee_id = autoAssignments.employee_id;
            }
            if (autoAssignments.mad_id !== undefined) {
                newPointing.mad_id = autoAssignments.mad_id;
            }
            if (autoAssignments.workplace_id !== undefined) {
                newPointing.workplace_id = autoAssignments.workplace_id;
            }
            if (autoAssignments.city !== undefined) {
                newPointing.city = autoAssignments.city;
            }

            // Show warnings if any
            if (autoAssignments.warnings && autoAssignments.warnings.length > 0) {
                autoAssignments.warnings.forEach((warning) => {
                    presentToast({
                        ...defaultToastOptions,
                        message: warning,
                        duration: 3000,
                        color: 'warning',
                    });
                });
            }
        }

        return newPointing;
    }, initialPointing);

    const employee = find((e) => e.id === pointing.employee_id, employees);
    const company = find((c) => c.id === employee?.company_id[0], companies);

    // Get visibility rules from shared constraints
    const visibilityRules = validator.getVisibilityRules(pointing);

    useEffect(() => {
        onChange?.(pointing);
    }, [pointing, onChange]);

    useImperativeHandle(
        ref,
        () => ({
            getPointing: () => pointing,
        }),
        [pointing],
    );

    const getHighlightColor = (...fields: string[]): string | undefined => {
        if (fieldsToHighlight && some(includes(__, fieldsToHighlight), fields)) {
            return highlightColor;
        }
    };

    return (
        <IonList lines="full" className="text-left pointing-detail-d">
            <IonListHeader>
                <IonLabel className="text-lg text-red-500 mt-0">Lieux</IonLabel>
            </IonListHeader>
            {ViewModeConstraints.isFieldVisible(viewMode, 'employee_selector') ? (
                <>
                    <Many2OneSelector
                        items={employees}
                        value={pointing.employee_id}
                        label="Employé"
                        icon={faHandshake}
                        backgroundColor={getHighlightColor('employee_id')}
                        selectOptions={{
                            placeholder: 'Employé',
                            interfaceOptions: {
                                header: 'Employé',
                                subHeader: 'Sélectionnez votre employé',
                                translucent: true,
                            },
                        }}
                        onChange={(value) => {
                            editPointing({
                                employee_id: value?.id,
                            });
                        }}
                        disabled={isDisabled}
                    />
                </>
            ) : ViewModeConstraints.isFieldVisible(viewMode, 'mad_selector') ? (
                <>
                    <Many2OneSelector
                        items={adjustMadsPropertiesByTime(madsPlaceholder, DateTime.fromISO(pointing.date))}
                        value={pointing.mad_id}
                        label="Client"
                        lines={pointing.mad_id === 0 ? 'none' : 'full'}
                        icon={faHandshake}
                        backgroundColor={getHighlightColor('mad_id')}
                        fieldToDisplay="display_name"
                        selectOptions={{
                            placeholder: 'Client',
                            interfaceOptions: {
                                header: 'Client',
                                subHeader: 'Sélectionnez votre client',
                                translucent: true,
                            },
                        }}
                        onChange={(value) => {
                            editPointing({
                                mad_id: value?.id,
                                employee_id: value?.employee_id[0],
                            });
                        }}
                        disabled={isDisabled}
                    />

                    {visibilityRules.showOtherMad && (
                        <>
                            <IonItem style={{ '--background': getHighlightColor('other_mad') || 'white' }}>
                                <div className="w-full">
                                    <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                                    <IonTextarea
                                        onInput={(e: any) =>
                                            editPointing({
                                                other_mad: e.target.value,
                                            })
                                        }
                                        aria-label="Autre MAD"
                                        placeholder="Si pas d'affaire, veuillez justifier"
                                        clearOnEdit={true}
                                        value={pointing.other_mad || ''}
                                        disabled={isDisabled}
                                        autoGrow={true}
                                    />
                                </div>
                            </IonItem>
                            {visibilityRules.showCompanySelector && (
                                <Many2OneSelector
                                    items={map(
                                        (e) => ({ id: e.id, name: e.company_id[1] }),
                                        // FIXME : What is that ? I don't understand what is suppose to do
                                        filter('company_id', employees) as typeof employees,
                                    )}
                                    value={pointing.employee_id}
                                    label="Choix de société"
                                    icon={faHandshake}
                                    selectOptions={{
                                        placeholder: 'Société',
                                        interfaceOptions: {
                                            header: 'Société',
                                            subHeader: 'Sélectionnez votre société',
                                            translucent: true,
                                        },
                                    }}
                                    onChange={(value) => {
                                        editPointing({
                                            employee_id: value?.id,
                                        });
                                    }}
                                    disabled={isDisabled}
                                />
                            )}
                        </>
                    )}
                </>
            ) : null}
            <Many2OneSelector
                items={WorkplaceConstraints.getAvailableWorkplaces(pointing.mad_id, workPlaces)}
                value={pointing.workplace_id}
                label="Chantier"
                lines={pointing.workplace_id === 0 ? 'none' : 'full'}
                icon={faMapLocationDot}
                selectOptions={{
                    placeholder: 'Chantier',
                    interfaceOptions: {
                        header: 'Chantier',
                        subHeader: 'Sélectionnez votre chantier',
                        translucent: true,
                    },
                }}
                backgroundColor={getHighlightColor('workplace_id')}
                onChange={(value) => {
                    if (value) {
                        editPointing({
                            workplace_id: value?.id,
                        });
                    }
                }}
                disabled={isDisabled}
            />
            {visibilityRules.showOtherWorkplace && (
                <IonItem style={{ '--background': getHighlightColor('other_workplace') || 'white' }}>
                    <div className="w-full">
                        <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                        <IonTextarea
                            placeholder="Veuillez indiquer l'adresse"
                            clearOnEdit={true}
                            aria-label="Autre chantier"
                            value={pointing.other_workplace || ''}
                            onIonChange={(e) => {
                                editPointing({
                                    other_workplace: e.detail.value || '',
                                });
                            }}
                            disabled={isDisabled}
                            autoGrow={true}
                        />
                    </div>
                </IonItem>
            )}
            {visibilityRules.showCity && (
                <IonItem style={{ '--background': getHighlightColor('city') || 'white' }}>
                    <IonLabel slot="start" className="!flex-none m-0">
                        <RoundedSquareIcon icon={faCity} />
                    </IonLabel>

                    <IonInput
                        placeholder="Ville du chantier"
                        value={pointing.city || ''}
                        onIonChange={(e) => {
                            editPointing({
                                city: e.detail.value || '',
                            });
                        }}
                        label="Ville"
                        className="!pl-1 text-right"
                        disabled={isDisabled}
                    />
                </IonItem>
            )}
            {employee && (
                <IonListHeader>
                    <IonLabel className="text-lg text-red-500">Heures</IonLabel>
                </IonListHeader>
            )}
            {viewMode === ViewMode.Confirm && (
                <TimeRangePicker
                    id={`confirm_real_time_${pointing.id}`}
                    title="Réelles"
                    timeRange={{
                        start: pointing.real_time_start,
                        end: pointing.real_time_end,
                    }}
                    disabled={true}
                    step={0}
                />
            )}

            <TimeRangePicker
                id={`confirm_input_time_${pointing.id}`}
                title="Pointées"
                backgroundColor={getHighlightColor('input_time_start', 'input_time_end')}
                timeRange={{
                    start: pointing.input_time_start,
                    end: pointing.input_time_end,
                }}
                onChange={(timeRange) => {
                    editPointing({
                        input_time_start: timeRange.start,
                        input_time_end: timeRange.end,
                    });
                }}
                disabled={isDisabled}
                showDuration
                step={appConfig?.roundingHours}
            />
            <VisibilityField company={company} companyAttributes={['pointing_rubric_working_hours_id']}>
                {getImplicitPlatform() == 'web' ? (
                    <IonItem style={{ '--background': 'white' }}>
                        <RoundedSquareIcon icon={faClock} color="bg-day" />
                        <IonLabel className={`pl-1 ${isDisabled ? 'item-interactive-disabled' : ''}`}>Jour</IonLabel>
                        <IonInput
                            type="time"
                            className="text-right"
                            value={hoursToDuration(pointing.working_hours).toFormat('hh:mm')}
                            aria-label="Heures travaillées de jour"
                            onIonChange={(e) => {
                                editPointing({
                                    working_hours: stringTimeToFloat(e.target.value?.toString()),
                                });
                            }}
                            disabled={isDisabled}
                        ></IonInput>
                    </IonItem>
                ) : (
                    <ItemWithHoursPicker
                        title="Jour"
                        iconProps={{ color: 'bg-day', icon: faClock }}
                        value={pointing.working_hours}
                        backgroundColor={getHighlightColor('working_hours')}
                        onChange={(value) => {
                            editPointing({
                                working_hours: value,
                            });
                        }}
                        disabled={isDisabled}
                        step={appConfig?.roundingHours}
                    />
                )}
            </VisibilityField>
            <VisibilityField company={company} companyAttributes={['pointing_rubric_working_night_hours_ids']}>
                {getImplicitPlatform() == 'web' ? (
                    <IonItem style={{ '--background': 'white' }}>
                        <RoundedSquareIcon icon={faClock} color="bg-night" />
                        <IonLabel className={`pl-1 ${isDisabled ? 'item-interactive-disabled' : ''}`}>Nuit</IonLabel>
                        <IonInput
                            type="time"
                            value={hoursToDuration(pointing.working_night_hours).toFormat('hh:mm')}
                            aria-label="Heures travaillées de nuit"
                            onIonChange={(e) => {
                                editPointing({
                                    working_night_hours: stringTimeToFloat(e.target.value?.toString()),
                                });
                            }}
                            disabled={isDisabled}
                            className="text-right"
                        ></IonInput>
                    </IonItem>
                ) : (
                    <ItemWithHoursPicker
                        title="Nuit"
                        iconProps={{ color: 'bg-night', icon: faClock }}
                        value={pointing.working_night_hours}
                        backgroundColor={getHighlightColor('working_night_hours')}
                        onChange={(value) => {
                            editPointing({
                                working_night_hours: value,
                            });
                        }}
                        disabled={isDisabled}
                        step={appConfig?.roundingHours}
                    />
                )}
            </VisibilityField>
            <VisibilityField company={company} companyAttributes={['pointing_rubric_bad_weather_hours_id']}>
                {getImplicitPlatform() == 'web' ? (
                    <IonItem style={{ '--background': 'white' }}>
                        <TriangleIcon
                            icon={faCloudBolt}
                            color="text-red-600"
                            style={
                                {
                                    '--fa-secondary-color': 'gold',
                                    '--fa-secondary-opacity': '1',
                                    '--fa-primary-color': '#e1e1e1',
                                } as React.CSSProperties
                            }
                        />
                        <IonLabel className={`pl-1 ${isDisabled ? 'item-interactive-disabled' : ''}`}>
                            Intempéries
                        </IonLabel>
                        <IonInput
                            type="time"
                            value={hoursToDuration(pointing.bad_weather_hours).toFormat('hh:mm')}
                            onIonChange={(e) => {
                                editPointing({
                                    bad_weather_hours: stringTimeToFloat(e.target.value?.toString()),
                                });
                            }}
                            disabled={isDisabled}
                            aria-label='Heures d"intempéries'
                            className="text-right"
                        ></IonInput>
                    </IonItem>
                ) : (
                    <ItemWithHoursPicker
                        title="Intempéries"
                        iconProps={{
                            icon: faCloudBolt,
                            color: 'text-red-600',
                            style: {
                                '--fa-secondary-color': 'gold',
                                '--fa-secondary-opacity': '1',
                                '--fa-primary-color': '#e1e1e1',
                            } as React.CSSProperties,
                        }}
                        value={pointing.bad_weather_hours}
                        backgroundColor={getHighlightColor('bad_weather_hours')}
                        onChange={(value) => {
                            editPointing({
                                bad_weather_hours: value,
                            });
                        }}
                        iconComponent={TriangleIcon}
                        disabled={isDisabled}
                        step={appConfig?.roundingHours}
                    />
                )}
            </VisibilityField>
            <VisibilityField company={company} companyAttributes={['pointing_rubric_absence_hours_id']}>
                {getImplicitPlatform() == 'web' ? (
                    <IonItem style={{ '--background': 'white' }}>
                        <RoundedSquareIcon icon={faCalendarXmark} color="bg-text-red-600" />
                        <IonLabel className={`pl-1 ${isDisabled ? 'item-interactive-disabled' : ''}`}>
                            Absences
                        </IonLabel>
                        <IonInput
                            type="time"
                            value={hoursToDuration(pointing.absence_hours).toFormat('hh:mm')}
                            aria-label="Heures d'absences"
                            onIonChange={(e) => {
                                editPointing({
                                    absence_hours: stringTimeToFloat(e.target.value?.toString()),
                                });
                            }}
                            disabled={isDisabled}
                            className="text-right"
                        ></IonInput>
                    </IonItem>
                ) : (
                    <ItemWithHoursPicker
                        title="Absences"
                        iconProps={{ color: 'bg-text-red-600', icon: faCalendarXmark }}
                        value={pointing.absence_hours}
                        backgroundColor={getHighlightColor('absence_hours')}
                        onChange={(value) => {
                            editPointing({
                                absence_hours: value,
                            });
                        }}
                        disabled={isDisabled}
                        step={appConfig?.roundingHours}
                    />
                )}
            </VisibilityField>
            <VisibilityField
                company={company}
                companyAttributes={['pointing_rubric_lunch_id', 'pointing_rubric_lunch_night_id']}
            >
                <IonListHeader>
                    <IonLabel className="text-lg text-red-500">Paniers</IonLabel>
                </IonListHeader>
                <VisibilityField company={company} companyAttributes={['pointing_rubric_lunch_id']}>
                    {getImplicitPlatform() == 'web' ? (
                        <IonItem style={{ '--background': 'white' }}>
                            <RoundedSquareIcon icon={faPlateUtensils} color="bg-day" transform="left-1" />
                            <IonLabel className={`pl-1 ${isDisabled ? 'item-interactive-disabled' : ''}`}>
                                Jour
                            </IonLabel>
                            <IonInput
                                type="text"
                                pattern="[0-9]"
                                placeholder="0"
                                aria-label="Panier repas de jour"
                                value={pointing.lunch}
                                min={0}
                                className="text-right"
                                onIonChange={(e) => {
                                    editPointing({
                                        lunch: toInteger(e.target.value),
                                    });
                                }}
                                disabled={isDisabled}
                            ></IonInput>
                        </IonItem>
                    ) : (
                        <ItemWithNumberPicker
                            title="Jour"
                            iconProps={{
                                icon: faPlateUtensils,
                                color: 'bg-day',
                            }}
                            value={pointing.lunch}
                            backgroundColor={getHighlightColor('lunch')}
                            disabled={isDisabled}
                            onChange={(value) => {
                                editPointing({
                                    lunch: value,
                                });
                            }}
                            max={LunchConstraints.getMaxLunchValue(appConfig)}
                        />
                    )}
                </VisibilityField>
                <VisibilityField company={company} companyAttributes={['pointing_rubric_lunch_night_id']}>
                    {getImplicitPlatform() == 'web' ? (
                        <IonItem style={{ '--background': 'white' }}>
                            <RoundedSquareIcon icon={faPlateUtensils} color="bg-night" transform="left-1" />
                            <IonLabel className={`pl-1 ${isDisabled ? 'item-interactive-disabled' : ''}`}>
                                Nuit
                            </IonLabel>
                            <IonInput
                                type="text"
                                pattern="[0-9]"
                                placeholder="0"
                                className="text-right"
                                aria-label="Panier repas de nuit"
                                value={pointing.lunch_night}
                                min={0}
                                onIonChange={(e) => {
                                    editPointing({
                                        lunch_night: toInteger(e.target.value),
                                    });
                                }}
                                disabled={isDisabled}
                            ></IonInput>
                        </IonItem>
                    ) : (
                        <ItemWithNumberPicker
                            title="Nuit"
                            iconProps={{ icon: faPlateUtensils, color: 'bg-night' }}
                            value={pointing.lunch_night}
                            disabled={isDisabled}
                            backgroundColor={getHighlightColor('lunch_night')}
                            onChange={(value) => {
                                editPointing({
                                    lunch_night: value,
                                });
                            }}
                            max={LunchConstraints.getMaxLunchValue(appConfig)}
                        />
                    )}
                </VisibilityField>
            </VisibilityField>
            <IonListHeader>
                <IonLabel className="text-lg text-red-500">Autres</IonLabel>
            </IonListHeader>
            <VisibilityField company={company} companyAttributes={['pointing_rubric_travel_ids']}>
                <IonItem style={{ '--background': getHighlightColor('travel') || 'white' }}>
                    <RoundedSquareIcon icon={faCar} />
                    <IonLabel>
                        <IonToggle
                            checked={!!pointing.travel}
                            color="success"
                            justify="space-between"
                            onIonChange={(e) => {
                                editPointing({
                                    travel: toInteger(e.detail.checked),
                                });
                            }}
                            disabled={isDisabled}
                            className="pl-1"
                        >
                            Déplacement
                        </IonToggle>
                    </IonLabel>
                </IonItem>
            </VisibilityField>
            {ViewModeConstraints.isFieldVisible(viewMode, 'employee_note', pointing.id) && (
                <IonItem
                    lines={viewMode === ViewMode.Adherent ? 'full' : 'none'}
                    style={{ '--background': getHighlightColor('employee_note') || 'white' }}
                >
                    <IonGrid className="pl-0">
                        <IonRow>
                            <RoundedSquareIcon icon={faPencil} />
                            <IonLabel className="pl-1 leading-7">Note</IonLabel>
                        </IonRow>
                        <IonRow>
                            <div className="w-full">
                                <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                                <IonTextarea
                                    value={pointing.employee_note || ''}
                                    autoGrow={true}
                                    aria-label="Note Employé"
                                    placeholder="Tapez quelque chose ici"
                                    onInput={(e: any) => {
                                        editPointing({
                                            employee_note: e.target.value,
                                        });
                                    }}
                                    disabled={
                                        isDisabled || !ViewModeConstraints.isFieldEditable(viewMode, 'employee_note')
                                    }
                                />
                            </div>
                        </IonRow>
                    </IonGrid>
                </IonItem>
            )}
            {ViewModeConstraints.isFieldVisible(viewMode, 'adherent_note') && (
                <IonItem lines="none" style={{ '--background': getHighlightColor('adherent_note') || 'white' }}>
                    <IonGrid className="pl-0">
                        <IonRow>
                            <RoundedSquareIcon icon={faPencil} />
                            <IonLabel className="pl-1 leading-7">Note Adhérent</IonLabel>
                        </IonRow>
                        <IonRow>
                            <div className="w-full">
                                <hr className="w-60 h-[2px] mx-auto my-1 bg-gray-200 border-0 rounded md:my-5" />
                                <IonTextarea
                                    value={pointing.adherent_note || ''}
                                    placeholder="Tapez quelque chose ici"
                                    aria-label="Note Adhérent"
                                    autoGrow={true}
                                    onInput={(e: any) => {
                                        editPointing({
                                            adherent_note: e.target.value,
                                        });
                                    }}
                                    disabled={isDisabled}
                                />
                            </div>
                        </IonRow>
                    </IonGrid>
                </IonItem>
            )}
        </IonList>
    );
};

export default forwardRef(PointingDetailMobile);
